import { Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { HttpService } from './http.service';
import { Observable, Subject, throwError } from 'rxjs';
import { SnackbarService } from '../snackbar.service';
import { MESSAGE_TYPE, XTENANT } from 'src/app/constant/constant';
import { TranslateService } from '@ngx-translate/core';
import { NccSpinner } from '../nccSpinner.service';
import { isArray } from 'util';
import { catchError, map, retry, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';

@Injectable()
export class APIServiceBaoCaoGDNN {
    constructor(
        private authenticationService: AuthenticationService,
        private http: HttpService,
        private message: SnackbarService,
        private translate: TranslateService,
        private _spinner: NccSpinner
    ) { }

    private _destroyed$ = new Subject();
    public ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    /**
     * Check client is Online
     * @param {boolean} showMessage default true
     * @return {boolean} true if is online
     */
    public isOnline(showMessage: boolean = true): boolean {
        if (!navigator.onLine) {
            this._spinner.hide();
            if (showMessage) {
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                );
            }
            return false;
        }
        return true;
    }

    /**
     * Function get: Get data by parameter
     * @param apiUrl
     * @param parameter
     */
    get(
        apiUrl: string = '',
        params: object = {},
        autoShowMessage: boolean = true,
    ): Observable<any> {
        if (this.isOnline()) {
            var option = this.getOptions();
            option['params'] = params;

            return this.http.get(apiUrl, option).pipe(
                map((res: Response) => {
                    return this.forwardData(res, res.status);
                }),
                catchError((error) => {
                    return this.forwardError(error, error.status, autoShowMessage);
                }),
                takeUntil(this._destroyed$)
            );
        } else {
            throw Observable.throw(
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                )
            );
        }
    }

    /** Function post: post data
     * @apiUrl URL reference to API
     */
    post(apiUrl: string = '', condition?: any, autoShowMessage: boolean = true) {
        if (this.isOnline()) {
            let normalizeData = this.normalizeDateValue(condition);
            let requestOptions = {
                headers: this.getJsonHeaders(),
                body: normalizeData == undefined ? '' : normalizeData,
                observe: 'response',
            };

            return this.http.request(apiUrl, 'POST', requestOptions).pipe(
                map((res: Response) => {
                    return this.forwardData(res, res.status);
                }),
                catchError((error) => {
                    return this.forwardError(error, error.status, autoShowMessage);
                }),
                takeUntil(this._destroyed$)
            );
        } else {
            throw throwError(
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                )
            );
        }
    }

    /** Function put: put data
     * @apiUrl URL reference to API
     */
    put(apiUrl: string = '', condition?: any, autoShowMessage: boolean = true) {
        if (this.isOnline()) {
            let normalizeData = this.normalizeDateValue(condition);
            let requestOptions = {
                headers: this.getJsonHeaders(),
                body: normalizeData == undefined ? '' : normalizeData,
                observe: 'response',
            };

            return this.http.request(apiUrl, 'PUT', requestOptions).pipe(
                map((res: Response) => {
                    return this.forwardData(res, res.status);
                }),
                catchError((error) => {
                    return this.forwardError(error, error.status, autoShowMessage);
                })
            );
        } else {
            throw throwError(
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                )
            );
        }
    }

    /** Function update: Add new data
     * @data Model used updated
     * @apiUrl URL reference to API
     */
    update(apiUrl: string = '', data, autoShowMessage: boolean = true) {
        if (this.isOnline()) {
            let normalizeData = this.normalizeDateValue(data);
            let requestOptions = {
                headers: this.getJsonHeaders(),
                body: normalizeData ? JSON.stringify(normalizeData) : '',
                observe: 'response',
            };
            return this.http.request(apiUrl, 'PUT', requestOptions).pipe(
                map((res: Response) => {
                    return this.forwardData(res, res.status);
                }),
                catchError((error) => {
                    return this.forwardError(error, error.status, autoShowMessage);
                })
            );
        } else {
            throw throwError(
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                )
            );
        }
    }

    /** Function delete: Delete data by key
     * @apiUrl URL reference to API, this is contain key
     */
    delete(apiUrl: string = '', data?: any, autoShowMessage: boolean = true) {
        if (this.isOnline()) {
            let requestOptions = {
                headers: this.getJsonHeaders(),
                observe: 'response',
                params: data,
            };

            return this.http.request(apiUrl, 'DELETE', requestOptions).pipe(
                map((res: Response) => {
                    return this.forwardData(res, res.status);
                }),
                catchError((error) => {
                    return this.forwardError(error, error.status, autoShowMessage);
                })
            );
        } else {
            throw throwError(
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                )
            );
        }
    }

    /**
     * Download file
     * @param apiUrl URL reference to API
     * @param fileName rename file for download
     * @param data form data
     * @param autoShowMessage
     */
    downloadFile(
        apiUrl: string,
        fileName: string,
        data?: any,
        autoShowMessage: boolean = true
    ) {
        if (this.isOnline()) {
            let normalizeData = this.normalizeDateValue(data);
            let requestOptions = {
                responseType: 'blob' as 'json',
                headers: this.getJsonHeaders(),
                body: normalizeData ? normalizeData : '',
            };

            return this.http
                .request(apiUrl, data ? 'POST' : 'GET', requestOptions)
                .pipe(
                    map(async (res) => {
                        let name =
                            fileName +
                            ' - ' +
                            new Date().toJSON().slice(0, 10).replace(/-/g, '');
                        this._spinner.hide();
                        let blob = res;
                        let dwldLink = document.createElement('a');
                        let url = URL.createObjectURL(blob);
                        let browser = window.navigator.userAgent.toLowerCase();
                        let isIE = false;
                        if (
                            browser.indexOf('msie') > -1 ||
                            browser.indexOf('edge/') > -1 ||
                            browser.indexOf('trident/') > -1
                        ) {
                            isIE = true;
                        }

                        if (Boolean(isIE)) {
                            window.navigator["msSaveBlob"](blob, name);
                        } else {
                            let isSafariBrowser =
                                navigator.userAgent.indexOf('Safari') != -1 &&
                                navigator.userAgent.indexOf('Chrome') == -1;
                            if (isSafariBrowser) {
                                let reader = new FileReader();
                                reader.onloadend = function (e) {
                                    window.location.href = reader.result as string;
                                };
                                reader.readAsDataURL(blob);
                            } else {
                                dwldLink.href = url;
                                dwldLink.download = fileName;
                                dwldLink.click();
                                dwldLink.remove();
                            }
                        }
                    }),
                    catchError((error) => {
                        return this.forwardError(error, error.status, autoShowMessage);
                    })
                );
        } else {
            throw throwError(
                this.message.showMessage(
                    this.translate.instant('MESSAGE.ERROR.E008'),
                    MESSAGE_TYPE.ERROR
                )
            );
        }
    }

    
check_arr(element,arr){
    let count = 0;
    for (let i = 0; i < arr.length; i ++){
        if (arr[i] === element)  {
            count ++;
            break
        }
    }
    return (count >0) ? true : false
}

    listXtenant = XTENANT;

    getOptions() {

        let headers = new HttpHeaders({
            Authorization: 'Bearer ' + this.authenticationService.getToken(),
            'Content-Type': 'application/json',
            'X-Tenant': 'so_ld'
            // 'Access-Control-Allow-Origin': '*',
        });
        return { headers: headers, observe: 'response' };
    }

    getJsonHeaders() {
        return new HttpHeaders({
            Authorization: 'Bearer ' + this.authenticationService.getToken(),
            'Content-Type': 'application/json',
            'X-Tenant': 'so_ld'
            // 'Access-Control-Allow-Origin': '*',
        });
    }

    protected processDownload(urlBlod: any, fileName: string) {
        setTimeout(function () {
            let dwldLink = document.createElement('a');
            dwldLink.href = urlBlod;
            dwldLink.download = fileName;
            dwldLink.click();
            dwldLink.remove();
            URL.revokeObjectURL(urlBlod);
        }, 1000);

        fetch(urlBlod).then();
        return;
    }
    /**
     * Request Error handler
     * @param {Response} error
     * @param status
     * @param autoShowMessage
     * @returns {ErrorObservable}
     */
    forwardError(error: HttpErrorResponse, status, autoShowMessage) {
        this._spinner.hide();

        if (status == 400) {
            //bad request (validate fail)
            return throwError(error.error);
        } else if (status == 401) {
            // unauthorized
            window.location.href = "/dang-nhap";
            return throwError(error.error);
        } else if (status == 403) {
            //forbidden
            window.location.href = '/error/insufficient-permission';
        } else if (status == 405) {
            // method not allow
            return throwError(error.error);
        } else if (status == 410) {
            window.location.href = "/Unavailable";
        } else if (status == 500) {
            // internal error
            return throwError(error.error);
        } else if (status == 404) {
            // window.location.href = '/error/page-not-found';
        }

        return throwError(error.error);
    }

    /**
     * Request Successful handler
     * @param {Response} res
     * @param {number} status
     * @returns {any}
     */
    private forwardData(res: Response, status: number): any {
        if (status == 200) {
            return res.body;
        } else if (status == 203) {
            return res.body;
        } else {
            return res.body;
        }
    }

    /**
     * Normalize date time value before send request to server
     * @param data
     */
    public normalizeDateValue(data: any): any {
        if (isArray(data)) {
            var normalizeData = Object.assign([], data);
            for (let index = 0; index < normalizeData.length; index++) {
                const element = normalizeData[index];
                normalizeData[index] = this.normalizeDateValue(element);
            }
            return normalizeData;
        } else {
            if (data instanceof Object) {
                var normalize = Object.assign({}, data);
                var keys = Object.keys(normalize);
                keys.forEach((key) => {
                    normalize[key] = this.normalizeDateValue(normalize[key]);
                });
                return normalize;
            }
            return data;
        }
    }
}